import React from "react";
import { Panel, Kicker } from "../ciWidgets";

export const ValuePoolsTab = ({ report }) => {
  const pools = report.value_pools || [];
  const dm = report.data_monetisation || {};

  return (
    <div className="space-y-6" data-testid="ci-tab-valuepools">
      <Panel className="p-7">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
          <Kicker>Quantified value pools</Kicker>
          <span className="text-[11px] text-amber-300">All values indicative — scenario estimates, never committed or guaranteed</span>
        </div>
        {report.overall?.value_creation_potential && (
          <div className="font-serif text-xl text-white mb-2">{report.overall.value_creation_potential}</div>
        )}
        {report.overall?.headline_suppressed && (
          <div className="mb-4 rounded-lg border border-amber-400/30 bg-amber-400/[0.06] px-4 py-2.5 text-[12px] text-amber-200/90" data-testid="ci-vp-headline-suppressed">
            Headline total suppressed by assurance controls — pools mix incompatible value bases and must not be summed into one number.
          </div>
        )}
        {report.assurance?.value_reconciliation?.note && (
          <div className="mb-6 rounded-lg border border-teal-400/20 bg-teal-400/[0.04] px-4 py-2.5 text-[12px] text-white/60" data-testid="ci-vp-reconciliation">
            <b className="text-teal-300">Reconciliation:</b> {report.assurance.value_reconciliation.note}
          </div>
        )}
        <div className="grid lg:grid-cols-2 gap-5">
          {pools.map((vp, i) => (
            <div key={i} className="rounded-xl border border-white/10 bg-[#060d18] p-6" data-testid={`ci-valuepool-card-${i}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="text-[15px] font-semibold text-white leading-snug">{vp.name}</div>
                  <div className="text-[11px] text-teal-300 uppercase tracking-[0.12em] font-bold mt-1">
                    {(vp.category || "").replace(/_/g, " ")}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    <span className="text-[9px] font-bold tracking-[0.1em] uppercase text-amber-200/90 border border-amber-400/25 rounded-full px-2 py-0.5">
                      {(vp.benefit_type || "annual recurring").replace(/_/g, " ")}
                    </span>
                    {vp.value_basis && (
                      <span className="text-[9px] font-bold tracking-[0.1em] uppercase text-white/45 border border-white/15 rounded-full px-2 py-0.5">
                        basis: {vp.value_basis}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-[10px] font-bold tracking-[0.1em] uppercase text-white/50 border border-white/15 rounded-full px-2.5 py-1 shrink-0">
                  {vp.confidence} confidence
                </span>
              </div>
              <div className="grid grid-cols-2 gap-x-5 gap-y-2 text-[12px] mb-4">
                <div><span className="text-white/40">Baseline:</span> <span className="text-white/70">{vp.baseline}</span></div>
                <div><span className="text-white/40">Benchmark:</span> <span className="text-white/70">{vp.benchmark}</span></div>
                <div className="col-span-2"><span className="text-white/40">Gap:</span> <span className="text-white/70">{vp.gap}</span></div>
                <div className="col-span-2"><span className="text-white/40">Lever:</span> <span className="text-white/70">{vp.lever}</span></div>
                <div className="col-span-2"><span className="text-white/40">Calculation logic:</span> <span className="text-white/60 italic">{vp.formula}</span></div>
              </div>
              <div className="rounded-lg bg-white/[0.04] border border-white/10 px-4 py-3 mb-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
                  {[
                    ["Annual benefit", vp.annual_benefit, "text-amber-300"],
                    ["Investment", vp.investment, "text-white/80"],
                    ["Payback", vp.payback, "text-white/80"],
                    ["Time to value", vp.time_to_value, "text-white/80"],
                  ].map(([l, v, c]) => (
                    <div key={l}>
                      <div className={`text-[13px] font-bold ${c}`}>{v || "—"}</div>
                      <div className="text-[9px] tracking-[0.14em] uppercase text-white/35 mt-0.5">{l}</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2 mb-4">
                {["conservative", "base", "stretch"].map((s) => (
                  <div key={s} className={`flex-1 rounded-lg border px-3 py-2 text-center ${s === "base" ? "border-teal-400/35 bg-teal-400/[0.06]" : "border-white/10"}`}>
                    <div className="text-[9px] tracking-[0.14em] uppercase text-white/40 mb-0.5">{s}</div>
                    <div className="text-[12px] text-white/80">{(vp.scenarios || {})[s] || "—"}</div>
                  </div>
                ))}
              </div>
              {(vp.assumptions || []).length > 0 && (
                <div className="text-[11px] text-white/40 leading-relaxed">
                  <b className="text-white/55">Assumptions:</b> {vp.assumptions.join("; ")}
                </div>
              )}
              {(vp.risks || []).length > 0 && (
                <div className="text-[11px] text-white/40 leading-relaxed mt-1">
                  <b className="text-red-300/70">Risks:</b> {vp.risks.join("; ")}
                </div>
              )}
            </div>
          ))}
        </div>
      </Panel>

      {dm.applicable && (
        <Panel className="p-7">
          <Kicker className="mb-2">Data-monetisation roadmap (18 months)</Kicker>
          <p className="text-[13px] text-white/55 mb-5 max-w-3xl">{dm.rationale}</p>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              ["Stage 1 · Data foundation", dm.stage_1_foundation],
              ["Stage 2 · Internal monetisation", dm.stage_2_internal],
              ["Stage 3 · External monetisation", dm.stage_3_external],
            ].map(([t, items]) => (
              <div key={t} className="rounded-lg border border-white/10 bg-[#060d18] p-5">
                <div className="text-[12px] font-bold text-teal-300 mb-3">{t}</div>
                <ul className="space-y-2">
                  {(items || []).map((x, i) => (
                    <li key={i} className="text-[12px] text-white/60 leading-relaxed">· {x}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          {dm.guardrails && (
            <div className="mt-4 rounded-lg border border-amber-400/25 bg-amber-400/[0.05] px-4 py-3 text-[12px] text-amber-100/80">
              <b>Guardrails:</b> {dm.guardrails}
            </div>
          )}
        </Panel>
      )}
    </div>
  );
};
